import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    PlusIcon,
    EllipsisVerticalIcon,
    PencilSquareIcon,
    KeyIcon,
    EyeIcon,
    ShieldCheckIcon,
    UserCircleIcon,
    CheckCircleIcon,
    XCircleIcon,
    NoSymbolIcon,
    PowerIcon,
    PaperAirplaneIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { useAuth } from "@/features/auth/state/useAuth";
import { extractApiError } from "@/lib/api-error";
import { getAdmins, getPermissionsCatalog, resendAdminInvitation } from "../api/adminsApi";
import { AdminStatusModal } from "../ui/AdminStatusModal";
import { DeleteAdminModal } from "../ui/DeleteAdminModal";
import { PermissionSummary } from "../ui/PermissionCatalogEditor";
import { ResetPasswordModal } from "../ui/ResetPasswordModal";
import type { Admin, AdminsQueryParams, AdminStatus, UpdateAdminStatusAction } from "../types";

const RoleBadge = ({ role = "ADMIN" }: { role?: string | null }) => {
    const normalizedRole = role ?? "ADMIN";
    const roleConfig = {
        SUPER_ADMIN: {
            bg: "bg-rose-100 dark:bg-rose-900/30",
            text: "text-rose-700 dark:text-rose-400",
            ring: "ring-rose-500/30",
            glow: "shadow-rose-500/20 shadow-lg",
        },
        ADMIN: {
            bg: "bg-emerald-100 dark:bg-emerald-900/30",
            text: "text-emerald-700 dark:text-emerald-400",
            ring: "ring-emerald-500/30",
            glow: "shadow-emerald-500/20 shadow-lg",
        },
        MODERATOR: {
            bg: "bg-blue-100 dark:bg-blue-900/30",
            text: "text-blue-700 dark:text-blue-400",
            ring: "ring-blue-500/30",
            glow: "",
        },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.MODERATOR;

    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${config.bg} ${config.text} ${config.ring} ${config.glow}`}
        >
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            {normalizedRole.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const statusStyles = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        DEACTIVATED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        PENDING_ACTIVATION: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    };

    const label = status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

    return (
        <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusStyles[status as keyof typeof statusStyles] || statusStyles.DEACTIVATED
                }`}
        >
            {label}
        </span>
    );
};

const InvitationBadge = ({ status }: { status: string }) => {
    const config = {
        PENDING: ["Invited", "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"],
        EXPIRED: ["Invitation expired", "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300"],
        ACTIVATED: ["Activated", "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"],
        NOT_INVITED: ["Existing account", "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300"],
    }[status as "PENDING" | "EXPIRED" | "ACTIVATED" | "NOT_INVITED"] ?? [status, "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300"];

    return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${config[1]}`}>{config[0]}</span>;
};

const parseUtcDate = (value: string | null | undefined) => {
    if (!value) return null;
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    const timestamp = Date.parse(hasTimezone ? value : `${value}Z`);
    return Number.isNaN(timestamp) ? null : timestamp;
};

const getStatusActions = (status: AdminStatus) => {
    if (status === "ACTIVE") {
        return [
            { action: "suspend" as const, label: "Suspend", icon: NoSymbolIcon },
            { action: "deactivate" as const, label: "Deactivate", icon: NoSymbolIcon },
        ];
    }

    if (status === "SUSPENDED") {
        return [{ action: "reactivate" as const, label: "Reactivate", icon: PowerIcon }];
    }

    if (status === "DEACTIVATED") {
        return [{ action: "activate" as const, label: "Activate", icon: PowerIcon }];
    }

    return [];
};

export const AdminsRoute = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const [filters, setFilters] = useState<AdminsQueryParams>({
        page: 0,
        size: 20,
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [statusDialog, setStatusDialog] = useState<{
        admin: Admin;
        action: UpdateAdminStatusAction;
    } | null>(null);
    const [deleteDialogAdmin, setDeleteDialogAdmin] = useState<Admin | null>(null);
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [now, setNow] = useState(() => Date.now());
    const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
    const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const isSuperAdmin = user?.role === "SUPER_ADMIN";

    const { data, isLoading, error } = useQuery({
        queryKey: ["admins", filters],
        queryFn: () => getAdmins(filters),
    });
    const permissionCatalogQuery = useQuery({
        queryKey: ["permissions-catalog"],
        queryFn: getPermissionsCatalog,
    });
    const resendMutation = useMutation({
        mutationFn: resendAdminInvitation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            setFlashMessage({ type: "success", text: "Invitation resent successfully." });
        },
        onError: (resendError) => setFlashMessage({ type: "error", text: extractApiError(resendError) }),
    });

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId && menuRefs.current[openMenuId]) {
                if (!menuRefs.current[openMenuId]?.contains(event.target as Node)) {
                    setOpenMenuId(null);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openMenuId]);

    useEffect(() => {
        if (!flashMessage) return;
        const timer = window.setTimeout(() => setFlashMessage(null), 5000);
        return () => window.clearTimeout(timer);
    }, [flashMessage]);

    useEffect(() => {
        const state = location.state as { flashMessage?: string } | null;
        if (state?.flashMessage) {
            setFlashMessage({ type: "success", text: state.flashMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    const handlePageChange = (newPage: number) => {
        setFilters((prev) => ({ ...prev, page: newPage }));
    };

    const handleEditClick = (admin: Admin) => {
        if (!admin.adminId) return;
        navigate(`/admin-management/${admin.adminId}/edit`, {
            state: { from: `${location.pathname}${location.search}` },
        });
        setOpenMenuId(null);
    };

    const handleResetPasswordClick = (admin: Admin) => {
        setSelectedAdmin(admin);
        setResetPasswordModalOpen(true);
        setOpenMenuId(null);
    };

    const handleViewDetails = (adminId: string) => {
        navigate(`/admin-management/${adminId}`);
        setOpenMenuId(null);
    };

    const formatDate = (dateString: string | null) => {
        const timestamp = parseUtcDate(dateString);
        if (timestamp === null) return "Never";
        return new Date(timestamp).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const admins = data?.data?.admins || [];
    const permissionCatalog = permissionCatalogQuery.data?.data.permissions;
    const filteredAdmins = searchQuery
        ? admins.filter(
            (admin) =>
                `${admin.fullName ?? `${admin.firstName} ${admin.lastName}`}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
                admin.email.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : admins;

    const totalPages = data?.data?.totalPages || 1;
    const currentPage = data?.data?.currentPage || 0;
    const totalElements = data?.data?.totalElements || 0;

    return (
        <div className="space-y-6">
            {flashMessage ? (
                <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {flashMessage.type === "success" ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <XCircleIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span>{flashMessage.text}</span>
                    </div>
                    <button
                        onClick={() => setFlashMessage(null)}
                        className="ml-4 rounded p-0.5 opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                        Admin Management
                    </h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Manage administrator accounts and their permissions
                    </p>
                </div>
                {isSuperAdmin ? (
                    <Button
                        onClick={() => navigate("/admin-management/create")}
                        className="inline-flex items-center gap-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Invite Admin
                    </Button>
                ) : null}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                            <UserCircleIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                Total entries
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {totalElements}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-100 dark:bg-rose-900/30">
                            <ShieldCheckIcon className="h-6 w-6 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                Super Admins
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {admins.filter((a) => a.role === "SUPER_ADMIN").length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
                            <CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                Active Admins
                            </p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {admins.filter((a) => a.status === "ACTIVE").length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex h-64 items-center justify-center">
                            <div className="text-center">
                                <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    Loading administrators...
                                </p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex h-64 items-center justify-center">
                            <div className="text-center">
                                <p className="text-sm text-rose-600 dark:text-rose-400">
                                    Failed to load administrators
                                </p>
                            </div>
                        </div>
                    ) : filteredAdmins.length === 0 ? (
                        <div className="flex h-64 items-center justify-center">
                            <div className="text-center">
                                <UserCircleIcon className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
                                <p className="mt-4 text-sm font-medium text-slate-900 dark:text-white">
                                    No administrators found
                                </p>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                    {searchQuery
                                        ? "Try adjusting your search query"
                                        : "Invite your first administrator to get started"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Admin
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Role
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Permissions
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Status
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Verified
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Created
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {filteredAdmins.map((admin) => {
                                    const isInvitation = !admin.adminId;
                                    const isCurrentAccount = admin.adminId === user?.userId;
                                    const statusActions = isInvitation ? [] : getStatusActions(admin.status);
                                    const rowKey = admin.adminId ?? admin.invitationId ?? admin.email;
                                    const displayName = admin.fullName || `${admin.firstName} ${admin.lastName}`.trim() || admin.email;
                                    const resendAt = parseUtcDate(admin.resendAvailableAt);
                                    const canResendNow = Boolean(
                                        isInvitation &&
                                        (admin.invitationStatus === "PENDING" || admin.invitationStatus === "EXPIRED") &&
                                        (!resendAt || resendAt <= now) &&
                                        (admin.canResendInvitation || Boolean(resendAt))
                                    );
                                    const resendSeconds = resendAt && resendAt > now ? Math.ceil((resendAt - now) / 1000) : 0;

                                    return (
                                    <tr
                                        key={rowKey}
                                        className="group transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3 text-left">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-sm font-semibold text-white">
                                                    {admin.firstName?.[0] ?? admin.email[0]?.toUpperCase()}
                                                    {admin.lastName?.[0] ?? ""}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900 dark:text-white">
                                                        {displayName}
                                                    </p>
                                                    <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                                        <span>{admin.email}</span>
                                                        {isCurrentAccount ? (
                                                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                                                Current account
                                                            </span>
                                                        ) : null}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <RoleBadge role={admin.role} />
                                        </td>
                                        <td className="px-6 py-4">
                                            <PermissionSummary
                                                permissions={admin.permissions}
                                                catalog={permissionCatalog}
                                                emptyLabel="No explicit permissions"
                                                maxVisible={2}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col items-start gap-1.5">
                                                {isInvitation ? <InvitationBadge status={admin.invitationStatus} /> : <StatusBadge status={admin.status} />}
                                                {!isInvitation && admin.invitationStatus === "ACTIVATED" ? <InvitationBadge status={admin.invitationStatus} /> : null}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {admin.emailVerified ? (
                                                <span className="inline-flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                                                    <CheckCircleIcon className="h-4 w-4" />
                                                    Verified
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-sm text-slate-400">
                                                    <XCircleIcon className="h-4 w-4" />
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                                            {isInvitation ? (
                                                <div>
                                                    <p>Sent {formatDate(admin.invitationLastSentAt)}</p>
                                                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Expires {formatDate(admin.invitationExpiresAt)}</p>
                                                </div>
                                            ) : formatDate(admin.createdAt)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div
                                                className="relative flex justify-end"
                                                ref={(el) => (menuRefs.current[rowKey] = el)}
                                            >
                                                {isInvitation ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => admin.invitationId && resendMutation.mutate(admin.invitationId)}
                                                        disabled={!canResendNow || resendMutation.isPending}
                                                        title={canResendNow ? "Resend invitation" : `Available in ${resendSeconds}s`}
                                                        className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 px-3 py-2 text-xs font-semibold text-primary-700 transition hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:disabled:border-slate-700 dark:disabled:text-slate-500"
                                                    >
                                                        <PaperAirplaneIcon className="h-4 w-4" />
                                                        {canResendNow ? "Resend invite" : `Resend in ${resendSeconds}s`}
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => setOpenMenuId(openMenuId === rowKey ? null : rowKey)}
                                                            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                                                        >
                                                            <EllipsisVerticalIcon className="h-5 w-5" />
                                                        </button>

                                                        {openMenuId === rowKey && (
                                                            <div className="absolute right-0 top-full z-10 mt-1 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleViewDetails(admin.adminId as string)}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                                                                >
                                                                    <EyeIcon className="h-4 w-4" />
                                                                    View Details
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEditClick(admin)}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                                                                >
                                                                    <PencilSquareIcon className="h-4 w-4" />
                                                                    Edit Profile
                                                                </button>
                                                                {isSuperAdmin && !isCurrentAccount ? (
                                                                    <>
                                                                        <button type="button" onClick={() => handleResetPasswordClick(admin)} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-amber-600 transition-colors hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"><KeyIcon className="h-4 w-4" />Reset Password</button>
                                                                        {statusActions.map(({ action, label, icon: Icon }) => <button type="button" key={action} onClick={() => { setStatusDialog({ admin, action }); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"><Icon className="h-4 w-4" />{label}</button>)}
                                                                        <button type="button" onClick={() => { setDeleteDialogAdmin(admin); setOpenMenuId(null); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"><TrashIcon className="h-4 w-4" />Delete Admin</button>
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Showing{" "}
                            <span className="font-medium">
                                {currentPage * (data?.data?.pageSize || 20) + 1}
                            </span>{" "}
                            to{" "}
                            <span className="font-medium">
                                {Math.min(
                                    (currentPage + 1) * (data?.data?.pageSize || 20),
                                    totalElements
                                )}
                            </span>{" "}
                            of <span className="font-medium">{totalElements}</span> results
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1}
                                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ResetPasswordModal
                isOpen={resetPasswordModalOpen}
                onClose={() => {
                    setResetPasswordModalOpen(false);
                    setSelectedAdmin(null);
                }}
                admin={selectedAdmin}
            />
            <AdminStatusModal
                isOpen={!!statusDialog}
                onClose={() => setStatusDialog(null)}
                admin={statusDialog?.admin ?? null}
                action={statusDialog?.action ?? null}
                onCompleted={(message) => setFlashMessage({ type: "success", text: message })}
            />
            <DeleteAdminModal
                isOpen={!!deleteDialogAdmin}
                onClose={() => setDeleteDialogAdmin(null)}
                admin={deleteDialogAdmin}
                onCompleted={(message) => setFlashMessage({ type: "success", text: message })}
            />
        </div>
    );
};
